import html2canvas from 'html2canvas'
import logoBadge from '../../../../../assets/smarttransyt-text-badge.png'
import {
  PHOTO_FRAME_WIDTH,
  PHOTO_FRAME_HEIGHT,
  LOGO_BOX_WIDTH,
  LOGO_BOX_HEIGHT,
  fittedImageRect,
  imageBoxSize
} from '../agent-photo-id-image-fit'
import { NAME_MIN_FONT_SIZE, fittedFontSize } from '../agent-photo-id-text-fit'
import { formatAgentPhotoIdSurnameFirst } from '../agent-photo-id'

// Must match the .agent-photo-id-card border-radius in template.vue.
const CARD_CORNER_RADIUS = 25
const CUT_GUIDE_WIDTH = 3
const CUT_GUIDE_COLOR = '#757575'

function strokeCutGuide (canvas) {
  const context = canvas.getContext('2d')
  const inset = CUT_GUIDE_WIDTH / 2
  const left = inset
  const top = inset
  const right = canvas.width - inset
  const bottom = canvas.height - inset
  const radius = CARD_CORNER_RADIUS - inset
  context.save()
  // html2canvas leaves its scale/translate on the context; draw in raw canvas pixels.
  context.setTransform(1, 0, 0, 1, 0, 0)
  context.strokeStyle = CUT_GUIDE_COLOR
  context.lineWidth = CUT_GUIDE_WIDTH
  context.beginPath()
  context.moveTo(left + radius, top)
  context.arcTo(right, top, right, bottom, radius)
  context.arcTo(right, bottom, left, bottom, radius)
  context.arcTo(left, bottom, left, top, radius)
  context.arcTo(left, top, right, top, radius)
  context.closePath()
  context.stroke()
  context.restore()
}

function paintFittedImage (source, boxWidth, boxHeight, fit, alignX, background) {
  const canvas = document.createElement('canvas')
  canvas.width = boxWidth
  canvas.height = boxHeight
  const context = canvas.getContext('2d')
  if (background) {
    context.fillStyle = background
    context.fillRect(0, 0, boxWidth, boxHeight)
  }
  const rect = fittedImageRect(source.naturalWidth, source.naturalHeight, boxWidth, boxHeight, fit, alignX)
  context.drawImage(source, rect.x, rect.y, rect.width, rect.height)
  return canvas.toDataURL('image/png')
}

function loadImageSrc (image, url) {
  return new Promise((resolve) => {
    image.onload = resolve
    image.onerror = resolve
    image.src = url
    if (image.complete) {
      resolve()
    }
  })
}

export default {
  name: 'agent-photo-id-card',
  props: {
    roleLabel: {
      type: String,
      default: ''
    },
    idNumber: {
      type: String,
      default: ''
    },
    fullName: {
      type: String,
      default: ''
    },
    photoSrc: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      logoSrc: logoBadge
    }
  },
  computed: {
    bannerName () {
      return formatAgentPhotoIdSurnameFirst(this.fullName)
    },
    infoColor () {
      const theme = this.$vuetify && this.$vuetify.theme
      return theme && theme.info ? theme.info : '#2196F3'
    }
  },
  methods: {
    whenReady () {
      const images = this.$el.querySelectorAll('img')
      const waits = []
      for (let i = 0; i < images.length; i++) {
        const image = images[i]
        if (image.complete) {
          continue
        }
        waits.push(new Promise((resolve) => {
          image.onload = resolve
          image.onerror = resolve
        }))
      }
      return Promise.all(waits)
    },
    placeFittedImage (image, boxEl, fallbackWidth, fallbackHeight, fit, alignX, background) {
      if (!image || !image.naturalWidth || !image.naturalHeight) {
        return Promise.resolve()
      }
      const box = imageBoxSize(boxEl, fallbackWidth, fallbackHeight)
      const url = paintFittedImage(image, box.width, box.height, fit, alignX, background)
      image.style.width = box.width + 'px'
      image.style.height = box.height + 'px'
      image.style.left = '0px'
      image.style.top = '0px'
      return loadImageSrc(image, url)
    },
    async bakeImageRatios () {
      const photo = this.$el.querySelector('.photo-frame img')
      const logo = this.$el.querySelector('.logo img')
      await this.placeFittedImage(photo, this.$el.querySelector('.photo-frame'), PHOTO_FRAME_WIDTH, PHOTO_FRAME_HEIGHT, 'cover', 'center', '#ffffff')
      await this.placeFittedImage(logo, this.$el.querySelector('.logo'), LOGO_BOX_WIDTH, LOGO_BOX_HEIGHT, 'contain', 'center', '#ffffff')
    },
    measureBannerName () {
      const bar = this.$el.querySelector('.name-bar')
      bar.style.fontSize = ''
      const barStyle = window.getComputedStyle(bar)
      return {
        textWidth: bar.querySelector('.name-text').getBoundingClientRect().width,
        availableWidth: bar.clientWidth - parseFloat(barStyle.paddingLeft) - parseFloat(barStyle.paddingRight),
        baseSize: parseFloat(barStyle.fontSize)
      }
    },
    fitNameToBanner () {
      // The bulk export reuses this component, so clear the size chosen for the previous agent.
      const name = this.measureBannerName()
      const size = fittedFontSize(name.textWidth, name.availableWidth, name.baseSize, NAME_MIN_FONT_SIZE)
      this.$el.querySelector('.name-bar').style.fontSize = size + 'px'
    },
    async capture () {
      await this.whenReady()
      await this.bakeImageRatios()
      this.fitNameToBanner()
      // html2canvas 1.0.0-rc.5 adds the page scroll offset when drawing, so x/y must be page
      // coordinates or the card is drawn shifted down by the scroll amount and clipped.
      const rect = this.$refs.card.getBoundingClientRect()
      const canvas = await html2canvas(this.$refs.card, {
        scale: 1,
        x: rect.left + window.pageXOffset,
        y: rect.top + window.pageYOffset,
        width: 1012,
        height: 638,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true
      })
      strokeCutGuide(canvas)
      return canvas
    }
  }
}
